import { WifiOff } from 'lucide-react'

interface AutoRefreshNoticeProps {
  errorMessage: string | null
  currentIntervalMs: number
  retryAfterSeconds?: number | null
  /** True while polling is suspended because the device is offline. */
  isPaused?: boolean
}

export default function AutoRefreshNotice({
  errorMessage,
  currentIntervalMs,
  retryAfterSeconds,
  isPaused = false,
}: AutoRefreshNoticeProps) {
  // Offline takes precedence over whatever error the last failed poll left
  // behind — "backing off" is technically true but describes a symptom, and
  // the cause is the one thing the user can act on.
  if (isPaused) {
    return (
      <div className="mb-6 flex items-start gap-3 rounded-lg border border-border bg-secondary/40 px-4 py-3 text-muted-foreground">
        <WifiOff className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
        <div>
          <div className="font-medium text-foreground">Live updates paused</div>
          <div className="mt-1 text-sm">
            You&apos;re offline — the figures below are the last ones we loaded. Refreshing resumes
            automatically when you reconnect.
          </div>
        </div>
      </div>
    )
  }

  if (!errorMessage) {
    return null
  }

  const nextRetrySeconds = retryAfterSeconds ?? Math.max(1, Math.ceil(currentIntervalMs / 1000))

  return (
    <div className="mb-6 rounded-lg border border-amber-300/50 bg-amber-50 px-4 py-3 text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100">
      <div className="font-medium">Automatic refresh is backing off</div>
      <div className="mt-1 text-sm">{errorMessage}</div>
      <div className="mt-1 text-xs opacity-80">
        Retrying automatically in about {nextRetrySeconds}s.
      </div>
    </div>
  )
}
